import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useDrafts } from "../../contexts/DraftsContext";
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  X,
  Printer,
  Share2,
  FileText,
  Loader2,
  Truck,
  Phone,
  MapPin,
  ArrowDownToLine,
  ArrowUpFromLine,
  Receipt,
  Wallet,
  StickyNote,
} from "lucide-react";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-GB", { hour12: false }); } catch { return iso; }
};
const fmtDay = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-GB"); } catch { return iso; }
};
// Split ISO date into day + time for a compact 2-line side badge next to
// each ledger row — much easier to scan than a single joined string.
const splitDate = (iso) => {
  if (!iso) return { day: "—", time: "" };
  try {
    const d = new Date(iso);
    return {
      day: d.toLocaleDateString("en-GB", { year: "2-digit", month: "2-digit", day: "2-digit" }),
      time: d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    };
  } catch { return { day: iso, time: "" }; }
};

// ─────────────────────────────── Supplier Form Modal ───────────────────────────────
const SupplierFormModal = ({ supplier, onClose, onSaved }) => {
  const isEdit = !!supplier?.id;
  const [name, setName] = useState(supplier?.name || "");
  const [phone, setPhone] = useState(supplier?.phone || "");
  const [address, setAddress] = useState(supplier?.address || "");
  const [note, setNote] = useState(supplier?.note || "");
  const [saving, setSaving] = useState(false);
  const { requestClose } = useDrafts();

  const hasContent = !isEdit && !!(name.trim() || phone.trim() || address.trim() || note.trim());
  const guardedClose = () => requestClose(hasContent, isEdit ? "تعديل مُجهّز" : "إضافة مُجهّز جديد", onClose);

  const submit = async () => {
    if (!name.trim()) { toast.error("اكتب اسم المُجهّز"); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), phone: phone.trim(), address: address.trim(), note: note.trim() };
      if (isEdit) {
        await axios.patch(`${API}/admin/suppliers/${supplier.id}`, payload);
        toast.success("تم تحديث بيانات المُجهّز");
      } else {
        await axios.post(`${API}/admin/suppliers`, payload);
        toast.success("تم إضافة المُجهّز");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[125] bg-black/60 flex items-center justify-center p-3" dir="rtl" data-testid="supplier-form-modal" onClick={guardedClose}>
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[92vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white p-4 flex justify-between items-center">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Truck className="w-5 h-5" /> {isEdit ? "تعديل مُجهّز" : "إضافة مُجهّز جديد"}
          </h3>
          <button onClick={guardedClose} data-testid="supplier-form-close" className="bg-white/20 hover:bg-white/30 rounded-full p-1.5">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <Field label="اسم المُجهّز *">
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="supplier-form-name"
              className="w-full px-3 py-2.5 rounded-xl border-2 border-[#3B82F6] focus:outline-none" />
          </Field>
          <Field label="رقم الهاتف">
            <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="supplier-form-phone" dir="ltr"
              className="w-full px-3 py-2.5 rounded-xl border-2 border-[#E4EBE6] focus:outline-none focus:border-[#3B82F6]" />
          </Field>
          <Field label="العنوان">
            <input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="supplier-form-address"
              className="w-full px-3 py-2.5 rounded-xl border-2 border-[#E4EBE6] focus:outline-none focus:border-[#3B82F6]" />
          </Field>
          <Field label="ملاحظة">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="supplier-form-note" rows={2}
              className="w-full px-3 py-2.5 rounded-xl border-2 border-[#E4EBE6] focus:outline-none focus:border-[#3B82F6]" />
          </Field>
        </div>
        <div className="p-3 border-t-2 border-[#E4EBE6] flex justify-end gap-2">
          <button onClick={guardedClose} className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2 px-4 rounded-full">
            إلغاء
          </button>
          <button onClick={submit} disabled={saving} data-testid="supplier-form-submit"
            className="bg-[#7ED321] hover:bg-[#6cb91d] disabled:bg-[#BAE6FD] text-white font-bold py-2 px-5 rounded-full flex items-center gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {isEdit ? "حفظ" : "إضافة"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-bold text-[#5C6E64] block mb-1">{label}</label>
    {children}
  </div>
);

// ─────────────────────────────── Voucher Modal ───────────────────────────────
const VoucherModal = ({ supplier, kind, onClose, onSaved }) => {
  const isPay = kind === "payment_out";
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  // When checked, the voucher amount also moves through the live cash box
  // (capital stays fixed). Default ON for payments — admin pays from cash
  // in 99% of cases — and OFF for receipt_in (less common).
  const [paidFromCashBox, setPaidFromCashBox] = useState(isPay);
  const { requestClose } = useDrafts();

  const hasContent = !!(amount.trim() || note.trim());
  const guardedClose = () => requestClose(hasContent, isPay ? "سند دفع للمُجهّز" : "سند استلام من المُجهّز", onClose);

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("أدخل مبلغًا صحيحًا"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/suppliers/${supplier.id}/vouchers`, {
        kind, amount: amt, note: note.trim(),
        paid_from_cash_box: paidFromCashBox,
      });
      toast.success(isPay ? "تم تسجيل سند الدفع" : "تم تسجيل سند الاستلام");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[130] bg-black/60 flex items-center justify-center p-3" dir="rtl" data-testid="voucher-modal" onClick={guardedClose}>
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className={`text-white p-4 flex justify-between items-center ${isPay ? "bg-gradient-to-l from-[#E85D45] to-[#F97316]" : "bg-gradient-to-l from-[#15803d] to-[#22c55e]"}`}>
          <h3 className="font-bold text-lg flex items-center gap-2">
            {isPay ? <ArrowUpFromLine className="w-5 h-5" /> : <ArrowDownToLine className="w-5 h-5" />}
            {isPay ? "سند دفع للمُجهّز" : "سند استلام من المُجهّز"}
          </h3>
          <button onClick={guardedClose} data-testid="voucher-close" className="bg-white/20 hover:bg-white/30 rounded-full p-1.5">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-[#F8FAFC] rounded-xl p-3 border-2 border-[#E4EBE6]">
            <p className="text-xs text-[#5C6E64]">المُجهّز</p>
            <p className="font-bold text-[#1E3A5F]">{supplier.name}</p>
            <p className="text-xs text-[#5C6E64] mt-1">
              المديونية الحالية:
              <span className="font-bold text-[#E85D45] mx-1" dir="ltr">{fmt(supplier.debt)} د.ع</span>
            </p>
          </div>
          <Field label="المبلغ (د.ع) *">
            <input type="number" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)}
              data-testid="voucher-amount" dir="ltr"
              className="w-full px-3 py-3 rounded-xl border-2 border-[#3B82F6] text-lg font-bold focus:outline-none" />
          </Field>
          <Field label="ملاحظة">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="voucher-note" rows={2}
              className="w-full px-3 py-2.5 rounded-xl border-2 border-[#E4EBE6] focus:outline-none focus:border-[#3B82F6]" />
          </Field>
          <label
            data-testid="voucher-cashbox-toggle"
            className={`flex items-start gap-2 cursor-pointer rounded-xl p-2.5 border-2 transition-colors ${
              paidFromCashBox
                ? "bg-[#ECFDF5] border-[#22C55E]"
                : "bg-[#F8FAFC] border-[#E4EBE6] hover:border-[#94A3B8]"
            }`}
          >
            <input
              type="checkbox"
              checked={paidFromCashBox}
              onChange={(e) => setPaidFromCashBox(e.target.checked)}
              data-testid="voucher-cashbox-checkbox"
              className="mt-0.5 w-4 h-4 accent-[#22C55E]"
            />
            <div className="flex-1">
              <p className="text-sm font-bold text-[#1E3A5F]">
                {isPay ? "خصم المبلغ من صندوق النقد 💰" : "إضافة المبلغ إلى صندوق النقد 💰"}
              </p>
              <p className="text-[11px] text-[#5C6E64] mt-0.5">
                {isPay
                  ? "إذا فعّلتها — راح ينقص رصيد الصندوق بمقدار المبلغ (دَفعة حقيقية كاش). إذا طفّيتها — يتعدّل الدين فقط."
                  : "إذا فعّلتها — راح يزيد رصيد الصندوق بمقدار المبلغ (استلام حقيقي كاش)."}
              </p>
            </div>
          </label>
          <p className="text-[11px] text-[#5C6E64] bg-[#FFF7ED] border border-[#FDBA74] rounded-xl p-2">
            {isPay
              ? "سند الدفع يقلّل المديونية المستحقة على جملتي للمُجهّز."
              : "سند الاستلام يقلّل المديونية أيضًا (مثلًا: استرجاع مبلغ زائد أو بضاعة مرتجعة)."}
          </p>
        </div>
        <div className="p-3 border-t-2 border-[#E4EBE6] flex justify-end gap-2">
          <button onClick={guardedClose} className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2 px-4 rounded-full">
            إلغاء
          </button>
          <button onClick={submit} disabled={saving} data-testid="voucher-submit"
            className={`text-white font-bold py-2 px-5 rounded-full flex items-center gap-1 disabled:bg-[#BAE6FD] ${isPay ? "bg-[#E85D45] hover:bg-[#d54d35]" : "bg-[#15803d] hover:bg-[#126b32]"}`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
            تسجيل السند
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────── Statement Modal ───────────────────────────────
const StatementModal = ({ supplierId, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voucherKind, setVoucherKind] = useState(null);
  const [editing, setEditing] = useState(false);
  const printRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/suppliers/${supplierId}/statement`);
      setData(res);
    } catch (e) {
      toast.error("فشل تحميل كشف الحساب");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [supplierId]);

  // Build merged sorted ledger
  const ledger = useMemo(() => {
    if (!data) return [];
    const rows = [];
    (data.invoices || []).forEach((inv) => {
      // Net invoice amount that becomes a debt (total - paid-at-invoice)
      const net = Number(inv.total || 0) - Number(inv.amount_paid || 0);
      if (Number(inv.total || 0) > 0) {
        rows.push({
          id: `inv-${inv.id}`,
          date: inv.created_at,
          type: "invoice",
          label: `فاتورة شراء #${inv.invoice_number || inv.id?.slice(0, 6)}`,
          debit: Number(inv.total || 0),  // أضافت دين على جملتي
          credit: Number(inv.amount_paid || 0),
          note: inv.note || "",
          net,
        });
      }
    });
    (data.vouchers || []).forEach((v) => {
      rows.push({
        id: `vch-${v.id}`,
        rawId: v.id,
        date: v.created_at,
        type: v.kind,
        label: v.kind === "payment_out" ? "سند دفع للمُجهّز" : "سند استلام من المُجهّز",
        debit: 0,
        credit: Number(v.amount || 0),
        note: v.note || "",
      });
    });
    rows.sort((a, b) => new Date(a.date) - new Date(b.date));
    let bal = 0;
    rows.forEach((r) => {
      bal += (r.debit || 0) - (r.credit || 0);
      r.balance = bal;
    });
    return rows;
  }, [data]);

  const deleteVoucher = async (vid) => {
    if (!(await confirmDialog("حذف هذا السند؟"))) return;
    try {
      await axios.delete(`${API}/admin/supplier-vouchers/${vid}`);
      toast.success("تم حذف السند");
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحذف");
    }
  };

  const handlePrint = () => {
    const node = printRef.current;
    if (!node) return;
    const w = window.open("", "_blank", "width=900,height=900");
    if (!w) { toast.error("فعّل النوافذ المنبثقة"); return; }
    w.document.write(`<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>كشف حساب المُجهّز - جملتي</title>
<style>
@page { size: A4; margin: 12mm }
* { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
body { font-family: 'Tajawal', system-ui, sans-serif; margin: 0; padding: 16px; color: #111; direction: rtl; }
h1 { color: #1E3A5F; margin: 0 0 4px; font-size: 22px; }
.sub { color: #5C6E64; font-size: 11px; margin-bottom: 12px; }
.totals { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 12px; }
.totals .b { background: #F8FAFC; border: 1px solid #E4EBE6; border-radius: 8px; padding: 8px; text-align: center; }
.totals .b p { margin: 0; }
.totals .b .l { font-size: 10px; color: #5C6E64; }
.totals .b .v { font-size: 14px; font-weight: 800; color: #1E3A5F; direction: ltr; }
.debt .v { color: #E85D45; }
table { width: 100%; border-collapse: collapse; }
th, td { border: 1px solid #E4EBE6; padding: 6px 8px; text-align: right; font-size: 12px; }
th { background: #1E3A5F; color: white; }
.num { direction: ltr; text-align: left; }
.debit { color: #E85D45; font-weight: 700; }
.credit { color: #15803d; font-weight: 700; }
.footer { margin-top: 18px; font-size: 11px; color: #5C6E64; text-align: center; }
</style></head><body>${node.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  };

  const buildShareText = () => {
    if (!data) return "";
    const s = data.supplier;
    const lines = [];
    lines.push("📒 كشف حساب مُجهّز — جملتي");
    lines.push(`المُجهّز: ${s.name}`);
    if (s.phone) lines.push(`الهاتف: ${s.phone}`);
    lines.push(`التاريخ: ${new Date().toLocaleDateString("en-GB")}`);
    lines.push("─".repeat(20));
    lines.push(`إجمالي فواتير الشراء: ${fmt(s.total_invoiced)} د.ع`);
    lines.push(`مدفوع عند الإستلام: ${fmt(s.total_paid_at_invoice)} د.ع`);
    lines.push(`إجمالي سندات الدفع: ${fmt(s.total_payment_out)} د.ع`);
    lines.push(`إجمالي سندات الاستلام: ${fmt(s.total_receipt_in)} د.ع`);
    lines.push("─".repeat(20));
    lines.push(`الرصيد المستحق: ${fmt(s.debt)} د.ع`);
    lines.push("https://jumlaty.online");
    return lines.join("\n");
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(buildShareText());
    const phone = (data?.supplier?.phone || "").replace(/[^0-9]/g, "");
    if (phone) {
      // Iraqi phones usually start with 0 — add 964 country code
      const intl = phone.startsWith("0") ? `964${phone.slice(1)}` : phone;
      window.open(`https://wa.me/${intl}?text=${text}`, "_blank");
    } else {
      window.open(`https://wa.me/?text=${text}`, "_blank");
    }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-3" dir="rtl" data-testid="supplier-statement-modal">
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[94vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white p-4 flex justify-between items-center shrink-0">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" /> كشف حساب المُجهّز
          </h3>
          <button onClick={onClose} data-testid="statement-close" className="bg-white/20 hover:bg-white/30 rounded-full p-1.5">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading || !data ? (
          <div className="flex-1 flex items-center justify-center p-10">
            <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
          </div>
        ) : (
          <>
            {/* Action bar */}
            <div className="p-3 border-b-2 border-[#E4EBE6] flex flex-wrap gap-2 items-center justify-between">
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setVoucherKind("payment_out")} data-testid="open-pay-voucher"
                  className="bg-[#E85D45] hover:bg-[#d54d35] text-white font-bold py-2 px-3 rounded-full flex items-center gap-1 text-sm">
                  <ArrowUpFromLine className="w-4 h-4" /> سند دفع
                </button>
                <button onClick={() => setVoucherKind("receipt_in")} data-testid="open-recv-voucher"
                  className="bg-[#15803d] hover:bg-[#126b32] text-white font-bold py-2 px-3 rounded-full flex items-center gap-1 text-sm">
                  <ArrowDownToLine className="w-4 h-4" /> سند استلام
                </button>
                <button onClick={() => setEditing(true)} data-testid="edit-supplier-btn"
                  className="bg-[#3B82F6] hover:bg-[#2563eb] text-white font-bold py-2 px-3 rounded-full flex items-center gap-1 text-sm">
                  <Edit2 className="w-4 h-4" /> تعديل البيانات
                </button>
              </div>
              <div className="flex gap-2">
                <button onClick={handlePrint} data-testid="statement-print"
                  className="bg-[#1E3A5F] hover:bg-[#152a45] text-white font-bold py-2 px-3 rounded-full flex items-center gap-1 text-sm">
                  <Printer className="w-4 h-4" /> طباعة / PDF
                </button>
                <button onClick={handleWhatsApp} data-testid="statement-whatsapp"
                  className="bg-[#25D366] hover:bg-[#1ebe57] text-white font-bold py-2 px-3 rounded-full flex items-center gap-1 text-sm">
                  <Share2 className="w-4 h-4" /> واتساب
                </button>
              </div>
            </div>

            {/* Printable area */}
            <div className="overflow-y-auto p-4 flex-1" ref={printRef}>
              <h1 className="text-2xl font-bold text-[#1E3A5F]">📒 كشف حساب المُجهّز — جملتي</h1>
              <div className="sub text-xs text-[#5C6E64] mb-3">
                <span className="font-bold text-[#111814]">{data.supplier.name}</span>
                {data.supplier.phone && <> · <Phone className="inline w-3 h-3" /> <span dir="ltr">{data.supplier.phone}</span></>}
                {data.supplier.address && <> · <MapPin className="inline w-3 h-3" /> {data.supplier.address}</>}
                {" · "} <span dir="ltr">{new Date().toLocaleDateString("en-GB")}</span>
              </div>

              {/* Totals */}
              <div className="totals grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                <Box l="إجمالي مشتريات" v={fmt(data.supplier.total_invoiced)} />
                <Box l="مدفوع عند الاستلام" v={fmt(data.supplier.total_paid_at_invoice)} color="#15803d" />
                <Box l="سندات الدفع" v={fmt(data.supplier.total_payment_out)} color="#15803d" />
                <Box l="المديونية المستحقة" v={fmt(data.supplier.debt)} color="#E85D45" big />
              </div>

              {/* Ledger Table */}
              {ledger.length === 0 ? (
                <p className="text-center text-[#5C6E64] py-8 text-sm">لا توجد حركات مالية مع هذا المُجهّز بعد.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th>التاريخ</th>
                      <th>الوصف</th>
                      <th>مدين</th>
                      <th>دائن</th>
                      <th>الرصيد</th>
                      <th className="print:hidden">إجراء</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((r) => {
                      const isInvoice = r.type === "invoice";
                      const isPayOut = r.type === "payment_out";
                      const isRecvIn = r.type === "receipt_in";
                      const dt = splitDate(r.date);
                      // Colored side date badge per row type
                      const dateBadgeCls =
                        isInvoice ? "border-[#E85D45] bg-[#FEE2E2] text-[#B91C1C]" :
                        isPayOut ? "border-[#F5A623] bg-[#FFF8EC] text-[#a06e10]" :
                        isRecvIn ? "border-[#22C55E] bg-[#DCFCE7] text-[#15803D]" :
                        "border-[#1E3A5F] bg-[#DBEAFE] text-[#1E3A5F]";
                      return (
                        <tr key={r.id} data-testid={`ledger-row-${r.id}`}>
                          <td className="num" data-testid={`ledger-date-${r.id}`}>
                            <div className={`inline-block rounded-lg border-2 px-2 py-1 text-center ${dateBadgeCls}`}>
                              <div className="text-[11px] font-black leading-none" dir="ltr">{dt.day}</div>
                              <div className="text-[9px] opacity-80 mt-0.5" dir="ltr">{dt.time}</div>
                            </div>
                          </td>
                          <td>
                            <div className="font-bold">{r.label}</div>
                            {r.note && <div className="text-[11px] text-[#5C6E64]">{r.note}</div>}
                          </td>
                          <td className="num debit">{r.debit ? fmt(r.debit) : "—"}</td>
                          <td className="num credit">{r.credit ? fmt(r.credit) : "—"}</td>
                          <td className="num font-bold">{fmt(r.balance)}</td>
                          <td className="print:hidden">
                            {r.rawId && (
                              <button onClick={() => deleteVoucher(r.rawId)}
                                data-testid={`del-voucher-${r.rawId}`}
                                className="text-[#E85D45] hover:bg-[#E85D45]/10 rounded p-1">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              <div className="footer mt-4 text-xs text-center text-[#5C6E64]">شكراً لتعاملكم مع جملتي · jumlaty.online</div>
            </div>
          </>
        )}

        {voucherKind && data && (
          <VoucherModal
            supplier={data.supplier}
            kind={voucherKind}
            onClose={() => setVoucherKind(null)}
            onSaved={() => { setVoucherKind(null); load(); onChanged && onChanged(); }}
          />
        )}

        {editing && data && (
          <SupplierFormModal
            supplier={data.supplier}
            onClose={() => setEditing(false)}
            onSaved={() => { setEditing(false); load(); onChanged && onChanged(); }}
          />
        )}
      </div>
    </div>
  );
};

const Box = ({ l, v, color = "#1E3A5F", big = false }) => (
  <div className={`b bg-[#F8FAFC] border-2 ${big ? "border-[#E85D45]" : "border-[#E4EBE6]"} rounded-xl p-2 text-center`}>
    <p className="text-[10px] text-[#5C6E64]">{l}</p>
    <p className={`font-black ${big ? "text-lg" : "text-sm"}`} dir="ltr" style={{ color }}>
      {v}
    </p>
  </div>
);

// ─────────────────────────────── Main Tab ───────────────────────────────
const SupplierAccountsTab = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [statementId, setStatementId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/suppliers`);
      setSuppliers(data || []);
    } catch {
      toast.error("فشل تحميل المُجهّزين");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return suppliers;
    return suppliers.filter((s) =>
      (s.name || "").toLowerCase().includes(t) ||
      (s.phone || "").includes(t) ||
      (s.address || "").toLowerCase().includes(t)
    );
  }, [suppliers, q]);

  const totals = useMemo(() => {
    const totalDebt = suppliers.reduce((acc, s) => acc + Math.max(0, Number(s.debt || 0)), 0);
    const indebtedCount = suppliers.filter((s) => Number(s.debt || 0) > 0).length;
    return { totalDebt, indebtedCount, count: suppliers.length };
  }, [suppliers]);

  const removeSupplier = async (s) => {
    if (!(await confirmDialog(`حذف المُجهّز "${s.name}"؟`))) return;
    try {
      await axios.delete(`${API}/admin/suppliers/${s.id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر الحذف");
    }
  };

  return (
    <div className="space-y-3" data-testid="supplier-accounts-tab" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <Truck className="w-5 h-5" />
          <h3 className="font-bold text-lg">حسابات المُجهّزين</h3>
        </div>
        <p className="text-xs opacity-90">تتبّع مديونيات جملتي للمُجهّزين، سجّل سندات الدفع/الاستلام، واستخرج كشف حساب تفصيلي.</p>
        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
          <div className="bg-white/15 rounded-xl py-2">
            <p className="text-[10px] opacity-80">عدد المُجهّزين</p>
            <p className="font-bold text-lg" dir="ltr">{fmt(totals.count)}</p>
          </div>
          <div className="bg-white/15 rounded-xl py-2">
            <p className="text-[10px] opacity-80">عليهم مديونية</p>
            <p className="font-bold text-lg" dir="ltr">{fmt(totals.indebtedCount)}</p>
          </div>
          <div className="bg-[#E85D45]/90 rounded-xl py-2">
            <p className="text-[10px] opacity-90">إجمالي الدين علينا</p>
            <p className="font-bold text-lg" dir="ltr">{fmt(totals.totalDebt)} د.ع</p>
          </div>
        </div>
      </div>

      {/* Search + Add */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث باسم المُجهّز / الهاتف / العنوان..."
            data-testid="supplier-search"
            className="w-full pr-10 pl-4 py-2.5 rounded-full border-2 border-[#E4EBE6] bg-white focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
        <button
          onClick={() => setShowAdd(true)}
          data-testid="add-supplier-btn"
          className="bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-2.5 px-4 rounded-full flex items-center gap-1"
        >
          <Plus className="w-4 h-4" /> إضافة مُجهّز
        </button>
      </div>

      {/* Suppliers list */}
      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin inline text-[#1E3A5F]" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-[#5C6E64] py-12 bg-white rounded-2xl border-2 border-dashed border-[#E4EBE6]">
          <Truck className="w-10 h-10 mx-auto mb-2 opacity-60" />
          <p className="text-sm">لا يوجد مُجهّزون. ابدأ بإضافة مُجهّز جديد.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {filtered.map((s) => {
            const debt = Number(s.debt || 0);
            const hasDebt = debt > 0.5;
            const isCredit = debt < -0.5;
            return (
              <div
                key={s.id}
                data-testid={`supplier-card-${s.id}`}
                className={`bg-white rounded-2xl p-3 border-2 transition-shadow shadow-sm hover:shadow-md ${
                  hasDebt ? "border-[#E85D45]/60" : isCredit ? "border-[#15803d]/60" : "border-[#E4EBE6]"
                }`}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#1E3A5F] truncate flex items-center gap-1">
                      <Truck className="w-4 h-4 text-[#5C6E64]" />
                      {s.name}
                    </p>
                    <div className="text-[11px] text-[#5C6E64] mt-1 space-y-0.5">
                      {s.phone && (
                        <p className="flex items-center gap-1"><Phone className="w-3 h-3" /> <span dir="ltr">{s.phone}</span></p>
                      )}
                      {s.address && (
                        <p className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.address}</p>
                      )}
                      {s.note && (
                        <p className="flex items-center gap-1"><StickyNote className="w-3 h-3" /> {s.note}</p>
                      )}
                      <p>عدد الفواتير: <span dir="ltr" className="font-bold text-[#1E3A5F]">{fmt(s.invoice_count)}</span></p>
                    </div>
                  </div>
                  <div className="text-left shrink-0">
                    <p className="text-[10px] text-[#5C6E64]">{hasDebt ? "علينا له" : isCredit ? "لنا عنده" : "الرصيد"}</p>
                    <p
                      className={`font-black text-lg ${hasDebt ? "text-[#E85D45]" : isCredit ? "text-[#15803d]" : "text-[#5C6E64]"}`}
                      dir="ltr"
                    >
                      {fmt(Math.abs(debt))} د.ع
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 mt-2 pt-2 border-t border-[#E4EBE6]">
                  <button
                    onClick={() => setStatementId(s.id)}
                    data-testid={`open-statement-${s.id}`}
                    className="flex-1 bg-[#1E3A5F] hover:bg-[#152a45] text-white text-xs font-bold py-1.5 rounded-full flex items-center justify-center gap-1"
                  >
                    <FileText className="w-3.5 h-3.5" /> كشف الحساب
                  </button>
                  <button
                    onClick={() => removeSupplier(s)}
                    data-testid={`del-supplier-${s.id}`}
                    className="bg-[#FEE2E2] hover:bg-[#FECACA] text-[#E85D45] text-xs font-bold py-1.5 px-2 rounded-full flex items-center justify-center"
                    title="حذف"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <SupplierFormModal
          supplier={null}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}

      {statementId && (
        <StatementModal
          supplierId={statementId}
          onClose={() => setStatementId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default SupplierAccountsTab;
